import React, { useEffect, useState } from 'react';
import { Globe, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import api from '../../lib/api';

export const TIMEZONES = ['UTC', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth', 'Australia/Hobart', 'Pacific/Auckland', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Asia/Kolkata', 'Asia/Dubai', 'Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto', 'America/Vancouver', 'America/Sao_Paulo', 'Africa/Johannesburg'];

const offsetLabel = (tz) => {
  try {
    const part = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch { return ''; }
};

// Org Profile → home timezone: one setting that warm-ups, nudges and digests all follow.
export default function OrgTimezoneField({ orgId, value, onSaved }) {
  const [tz, setTz] = useState(value || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setTz(value || ''); }, [value]);
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const options = [...new Set([tz, browserTz, ...TIMEZONES].filter(Boolean))];

  const save = async () => {
    if (!orgId || !tz) return;
    setSaving(true);
    try {
      await api.put(`/rbac/organizations/${orgId}`, { timezone: tz });
      onSaved?.(tz);
      toast.success(`Home timezone set to ${tz} — warm-ups, nudges and digests now follow local time`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save timezone'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="org-timezone-field">
      <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
        <Globe className="h-4 w-4 text-slate-500" /> Home timezone
      </Label>
      <div className="flex items-center gap-2 mt-1">
        <select
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800"
          data-testid="org-timezone-select"
        >
          {!tz && <option value="">Not set (UTC)</option>}
          {options.map((z) => <option key={z} value={z}>{z}{z === browserTz ? ' · your browser' : ''} {offsetLabel(z) && `(${offsetLabel(z)})`}</option>)}
        </select>
        <Button type="button" variant="outline" size="sm" onClick={save} disabled={saving || !tz || tz === (value || '')} className="shrink-0" data-testid="save-org-timezone-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-xs text-slate-400 mt-1">Server cache warm-up (default 03:00), roadmap owner nudges (07:00), story forecast alerts (06:00) and the Monday driver-owner digest (08:00) all run on this clock.</p>
    </div>
  );
}
